import { useEffect, useRef, useState } from 'react';
import {
  RotateCw,
  Bell,
  Camera,
  Video,
  Square,
  ScrollText,
  X,
  Minus,
  Maximize2,
  Minimize2,
  RotateCcw,
  RefreshCw,
  ScreenShare,
  ScreenShareOff,
  Settings,
  AppWindow,
  Activity,
  Coffee,
  Clipboard,
  PictureInPicture2,
  Pin,
  PinOff,
  Trash2,
  AlertTriangle,
  Volume2,
  VolumeX,
  Power,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import clsx from 'clsx';
import { adbApi, filesApi } from '@/lib/api';
import {
  KEYCODE,
  KEY_ACTION,
  packCollapsePanels,
  packExpandNotificationPanel,
  packExpandSettingsPanel,
  packInjectKeycode,
  packRotateDevice,
  packSetDisplayPower,
} from '@/lib/scrcpy/control';
import {
  useMirror,
  SIDE_TOOLS_MIN_W,
  SIDE_TOOLS_MAX_W,
  SIDE_TOOLS_LABEL_THRESHOLD,
  type LockOrientation,
} from '@/store/mirror';
import type { MirrorCanvasHandle, MirrorStatus } from './MirrorCanvas';

/**
 * The floating mirror's chrome lives in TWO independent surfaces. The
 * old bottom `MirrorConfigBar` was deleted: aspect-lock made the 1:1
 * button redundant, and quality / reconnect / rotate / orientation
 * lock / stay-awake were each redistributed to a more natural home.
 *
 *   ┌─────────────────────────────────────────────┐
 *   │ MirrorDragHeader  📱 [quality] ↺  ⛶ ⊟ 📌 _ ✕ │  ← device + window-shape controls
 *   ├──────────────────────────────────────────┬──┤
 *   │ phone-frame (canvas)                     │ S │  ← MirrorSideTools (vertical actions)
 *   │  ◁ ○ □  (nav chrome)                     │ I │     ▾ 方向 / ▾ 硬件键 / ▾ 系统 /
 *   │                                          │ D │     ▾ 剪贴板 / ▾ 捕获 / ▾ App /
 *   │                                          │   │     ▾ 其他       (each collapsible)
 *   └──────────────────────────────────────────┴──┘
 *
 * Shared helpers (`ToolbarBtn` / `WindowBtn` / `ClearDataModal` / format*)
 * live in this file too because there's no other consumer.
 */

// `maxSize: -1` 是"自动"哨兵：SPA 不传 maxSize/maxFps，让服务端按 transport
// 自动选档（USB → 1080p/60fps/8M, wireless → 900p/30fps/2.5M）。这是默认值，
// 避免在无线上死命塞 1080p/60fps 导致卡顿。其他档位都是显式覆盖（含 maxFps）。
const QUALITY_PRESETS = [
  { label: '自动（按连接方式）', maxSize: -1, maxFps: -1 },
  { label: '高清 1080', maxSize: 1080, maxFps: 60 },
  { label: '标清 720', maxSize: 720, maxFps: 60 },
  { label: '流畅 540', maxSize: 540, maxFps: 60 },
  { label: '原生', maxSize: 0, maxFps: 60 },
];

function describeStatus(status: MirrorStatus): string {
  if (status.kind === 'live') {
    return `${status.meta.deviceName} · ${status.meta.w}×${status.meta.h}`;
  }
  if (status.kind === 'connecting') {
    return status.retry
      ? `正在重新连接手机画面… (${status.retry.count}/${status.retry.max})`
      : '正在连接手机画面…';
  }
  if (status.kind === 'error') {
    return `错误：${status.message}`;
  }
  return '不支持 WebCodecs';
}

/* ===================================================================== */
/* 1) DragHeader — 顶部窄栏：设备 + 分辨率 + 重连 + 窗口形态 + 最小化/关闭 */
/* ===================================================================== */

interface DragHeaderProps {
  status: MirrorStatus;
  canvas: React.RefObject<MirrorCanvasHandle>;
  onDragStart: (e: React.PointerEvent) => void;
  /** True when the toolbar should NOT act as a drag handle (fullscreen / PiP). */
  dragDisabled: boolean;
  /** Phase 6: browser exposes `documentPictureInPicture`. */
  supportsDocPip: boolean;
  /** Phase 6: mirror currently rendered into a PiP window. */
  inPip: boolean;
  /** Phase 6: toggle PiP. No-op when `supportsDocPip` is false. */
  onTogglePip: () => void | Promise<void>;
}

export function MirrorDragHeader({
  status,
  canvas,
  onDragStart,
  dragDisabled,
  supportsDocPip,
  inPip,
  onTogglePip,
}: DragHeaderProps): JSX.Element {
  const close = useMirror((s) => s.close);
  const setMinimized = useMirror((s) => s.setMinimized);
  const isFullscreen = useMirror((s) => s.isFullscreen);
  const toggleFullscreen = useMirror((s) => s.toggleFullscreen);
  const pinned = useMirror((s) => s.pinned);
  const togglePinned = useMirror((s) => s.togglePinned);
  const quality = useMirror((s) => s.quality);
  const setQuality = useMirror((s) => s.setQuality);

  return (
    <div
      className={clsx(
        // Header band: tinted with --accent so it visually separates
        // from the mirror body (`bg-bg-card`) AND echoes the clipboard
        // panel's header stripe — same band sits across both floating
        // surfaces, anchoring them as part of the same window family.
        // backdrop-blur softens whatever's bleeding through (status
        // bar, video frame at the very top edge during fullscreen).
        'relative flex h-8 shrink-0 select-none items-center gap-1 border-b border-accent/25 bg-accent/10 backdrop-blur-sm pl-2 pr-3 text-xs text-ink-muted',
        // No drag in fullscreen / PiP — the window owns the whole viewport
        // (or is sized by the OS in PiP), dragging the toolbar by mistake
        // would surface a confusing ghost position once we leave.
        dragDisabled ? 'cursor-default' : 'cursor-move',
      )}
      onPointerDown={dragDisabled ? undefined : onDragStart}
    >
      <span className="min-w-0 max-w-[40%] truncate">{describeStatus(status)}</span>
      {/*
        Quality select moved up here from the deleted bottom config bar
        — picking a resolution always implies a server reconnect, so it
        sits next to the explicit "重连" button as a single window-level
        cluster. `text-[11px]` keeps it visually subordinate to the
        device name.
      */}
      <select
        className="ml-1 h-6 rounded border border-line bg-bg px-1.5 text-[11px] text-ink"
        value={quality.maxSize}
        onChange={(e) => {
          const n = QUALITY_PRESETS.find((p) => String(p.maxSize) === e.target.value);
          if (n) setQuality({ maxSize: n.maxSize, maxFps: n.maxFps });
        }}
        onPointerDown={(e) => e.stopPropagation()}
        title="切换分辨率（重连）"
      >
        {QUALITY_PRESETS.map((p) => (
          <option key={p.label} value={p.maxSize}>
            {p.label}
          </option>
        ))}
      </select>
      <WindowBtn
        icon={RotateCcw}
        label="重新连接画面"
        onClick={() => canvas.current?.reconnect()}
      />
      <span className="ml-auto" />
      {/*
        Window-shape cluster — controls how the floating window itself
        is laid out. Pin and PiP coexist (Pin = z-index above SPA,
        PiP = OS-level standalone window): they're orthogonal so the
        old either-or branching was hiding Pin from Chrome / Edge
        users who actually wanted z-index pinning *without* leaving
        the browser.
      */}
      <WindowBtn
        icon={isFullscreen ? Minimize2 : Maximize2}
        label={isFullscreen ? '退出全屏 (ESC)' : '全屏'}
        active={isFullscreen}
        onClick={toggleFullscreen}
      />
      {supportsDocPip && (
        <WindowBtn
          icon={PictureInPicture2}
          label={inPip ? '收回到主窗口' : '脱离窗口（OS 级置顶）'}
          active={inPip}
          onClick={() => void onTogglePip()}
        />
      )}
      <WindowBtn
        icon={pinned ? PinOff : Pin}
        label={
          inPip
            ? '已脱离窗口，由 OS 接管置顶'
            : pinned
              ? '取消钉住（恢复默认层级）'
              : '钉住到最上层（盖住 SPA 内的弹窗 / Toast）'
        }
        active={pinned}
        onClick={togglePinned}
        disabled={inPip}
      />
      <span className="mx-1 h-3 w-px bg-line/70" />
      {/*
        "最小化" 把浮窗收回左侧栏的设备图标里。视频流和 WS 连接保持，
        再次点击侧栏图标即可瞬时恢复画面。"关闭" 才真正断开 scrcpy。
      */}
      <WindowBtn
        icon={Minus}
        label="最小化到侧栏"
        onClick={() => setMinimized(true)}
      />
      <WindowBtn icon={X} label="关闭" onClick={close} variant="danger" />
    </div>
  );
}

/* ===================================================================== */
/* 2) SideTools — 右侧垂直工具栏                                          */
/* ===================================================================== */

interface SideToolsProps {
  serial: string;
  status: MirrorStatus;
  canvas: React.RefObject<MirrorCanvasHandle>;
  /** Open the dual-column clipboard history drawer (replaces the old pull/paste pair). */
  onOpenClipHistory: () => void;
  /** True iff the drawer is currently visible — used to highlight the toolbar button. */
  clipHistoryOpen: boolean;
}

export function MirrorSideTools({
  serial,
  status,
  canvas,
  onOpenClipHistory,
  clipHistoryOpen,
}: SideToolsProps): JSX.Element {
  const toggleLogcat = useMirror((s) => s.toggleLogcat);
  const showLogcat = useMirror((s) => s.showLogcat);
  const hudVisible = useMirror((s) => s.hudVisible);
  const toggleHud = useMirror((s) => s.toggleHud);
  const stayAwake = useMirror((s) => s.stayAwake);
  const setStayAwake = useMirror((s) => s.setStayAwake);
  const lockOrientation = useMirror((s) => s.lockOrientation);
  const setLockOrientation = useMirror((s) => s.setLockOrientation);
  const sideToolsCollapsed = useMirror((s) => s.sideToolsCollapsed);
  const toggleSideToolsGroup = useMirror((s) => s.toggleSideToolsGroup);

  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  /**
   * Phase 7 — "clear app data" confirmation modal. Self-contained (no
   * external component library): rendered inline below so we stay inside
   * the toolbar's portal layer and don't need to thread a global modal
   * provider through floating mirrors that might live in a PiP window.
   */
  const [clearConfirm, setClearConfirm] = useState<{ pkg: string } | null>(null);
  /** Generic "App busy" lock for the three buttons in the App group. */
  const [appBusy, setAppBusy] = useState(false);
  /**
   * Local "we think the device display is off" hint. The mirror server
   * doesn't tell us when the panel power state changes (user pressing
   * the physical power key, for instance), so this is best-effort: the
   * user clicks our toggle, we flip our guess + send the protocol byte.
   * If the two get out of sync, clicking again will resync.
   */
  const [displayOff, setDisplayOff] = useState(false);
  /**
   * Local "which system panel did we last open" tracker. Makes the
   * notification & quick-settings buttons act as toggles instead of
   * fire-and-forget expand requests — second click collapses what we
   * just opened. Manual swipe on the device goes undetected; clicking
   * again resyncs with a fresh COLLAPSE + EXPAND.
   */
  const [expandedPanel, setExpandedPanel] = useState<'none' | 'notif' | 'settings'>('none');

  const live = status.kind === 'live';
  const sendControl = (bytes: Uint8Array): void => canvas.current?.sendControl(bytes);
  const tapKey = (keycode: number): void => {
    sendControl(
      packInjectKeycode({ action: KEY_ACTION.DOWN, keycode, repeat: 0, metaState: 0 }),
    );
    sendControl(
      packInjectKeycode({ action: KEY_ACTION.UP, keycode, repeat: 0, metaState: 0 }),
    );
  };

  const flashToast = (msg: string): void => {
    setToast(msg);
    window.setTimeout(() => setToast((cur) => (cur === msg ? null : cur)), 2200);
  };

  /**
   * Loose heuristic for "this package is a launcher / system shell, so
   * the App group buttons would do something nonsensical on it"
   * (relaunching the launcher = restart your whole homescreen, clearing
   * its data = nuke icon layout & wallpaper). Includes AOSP defaults,
   * Pixel, Xiaomi, Huawei, OPPO/Realme, Samsung, plus the universal
   * `systemui` package which can also briefly take focus during dialogs.
   */
  const LAUNCHER_PKGS = new Set([
    'com.android.launcher',
    'com.android.launcher2',
    'com.android.launcher3',
    'com.google.android.apps.nexuslauncher', // Pixel
    'com.miui.home', // Xiaomi
    'com.huawei.android.launcher', // Huawei
    'com.oppo.launcher', // OPPO
    'com.coloros.launcher', // OPPO/Realme ColorOS
    'com.bbk.launcher2', // vivo
    'com.sec.android.app.launcher', // Samsung
    'com.android.systemui',
  ]);
  const isLauncherPkg = (pkg: string): boolean => LAUNCHER_PKGS.has(pkg);

  /**
   * Common prologue for the App group: resolve the foreground package
   * via `app.foreground`, bail when nothing is in focus or when the
   * launcher is on top (with a friendly toast), then delegate to the
   * caller. Each callsite still does its own try/catch so we don't
   * paper over per-action errors.
   */
  const withForegroundPkg = async (
    action: (pkg: string) => Promise<void>,
  ): Promise<void> => {
    if (appBusy) return;
    setAppBusy(true);
    try {
      flashToast('正在识别前台应用…');
      const { result } = await adbApi.run<{ packageName?: string; activity?: string }>({
        serial,
        name: 'app.foreground',
      });
      const pkg = result.packageName;
      if (!pkg) {
        flashToast('未识别到前台应用');
        return;
      }
      if (isLauncherPkg(pkg)) {
        flashToast(`当前是桌面（${pkg}），请先打开 App`);
        return;
      }
      await action(pkg);
    } catch (e) {
      flashToast(`操作失败：${(e as Error).message}`);
    } finally {
      setAppBusy(false);
    }
  };

  const onRestartApp = (): void => {
    void withForegroundPkg(async (pkg) => {
      await adbApi.run({ serial, name: 'app.stop', args: { package: pkg } });
      await adbApi.run({ serial, name: 'app.start', args: { package: pkg } });
      flashToast(`已重启 ${pkg}`);
    });
  };

  const onRequestClearData = (): void => {
    void withForegroundPkg(async (pkg) => {
      setClearConfirm({ pkg });
    });
  };

  const onConfirmClearData = async (): Promise<void> => {
    const pkg = clearConfirm?.pkg;
    setClearConfirm(null);
    if (!pkg) return;
    try {
      flashToast(`正在清除 ${pkg} 的数据…`);
      await adbApi.run({ serial, name: 'app.clear', args: { package: pkg } });
      flashToast(`已清除 ${pkg} 的数据`);
    } catch (e) {
      flashToast(`清数据失败：${(e as Error).message}`);
    }
  };

  const onOpenAppSettings = (): void => {
    void withForegroundPkg(async (pkg) => {
      await adbApi.run({ serial, name: 'app.openSettings', args: { package: pkg } });
      flashToast(`已打开 ${pkg} 的应用设置`);
    });
  };

  const onToggleNotifPanel = (): void => {
    if (!live) return;
    if (expandedPanel === 'notif') {
      sendControl(packCollapsePanels());
      setExpandedPanel('none');
    } else {
      sendControl(packExpandNotificationPanel());
      setExpandedPanel('notif');
    }
  };

  const onToggleSettingsPanel = (): void => {
    if (!live) return;
    if (expandedPanel === 'settings') {
      sendControl(packCollapsePanels());
      setExpandedPanel('none');
    } else {
      sendControl(packExpandSettingsPanel());
      setExpandedPanel('settings');
    }
  };

  const onToggleDisplayPower = (): void => {
    if (!live) {
      flashToast('画面尚未连接，无法切换设备屏幕');
      return;
    }
    const wantOn = displayOff;
    sendControl(packSetDisplayPower(wantOn));
    setDisplayOff(!wantOn);
    flashToast(wantOn ? '已点亮设备屏幕' : '已关闭设备屏幕（镜像继续）');
  };

  const onScreenshot = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await adbApi.run({ serial, name: 'screen.capture' });
      flashToast('已保存截屏到 PC');
    } catch (e) {
      flashToast(`截屏失败：${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Record the floating mirror's canvas via `MediaRecorder`. We avoid
   * `screen.record.start` (device-side `screenrecord`) because restrictive
   * vendor SELinux policies on Realme/OPPO/OnePlus block it from writing
   * any path *and* block the shell from killing it. The canvas already
   * has the decoded frames, so capturing them in-browser is more reliable
   * and works on every device the mirror works on.
   */
  const recStartRef = useRef<number>(0);
  const [recElapsed, setRecElapsed] = useState(0);
  useEffect(() => {
    if (!recording) {
      setRecElapsed(0);
      return;
    }
    recStartRef.current = Date.now();
    const id = window.setInterval(
      () => setRecElapsed(Math.floor((Date.now() - recStartRef.current) / 1000)),
      500,
    );
    return () => window.clearInterval(id);
  }, [recording]);

  const onToggleRecord = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      if (!recording) {
        if (status.kind !== 'live') {
          flashToast('画面尚未连接,无法开始录屏');
          return;
        }
        const picked = canvas.current?.startRecording({ fps: 30, bitsPerSecond: 8_000_000 });
        if (!picked) {
          flashToast('当前浏览器不支持画面录制（需要 MediaRecorder + canvas.captureStream）');
          return;
        }
        setRecording(true);
        flashToast(`开始录屏（${picked.ext.toUpperCase()}）`);
      } else {
        const result = await canvas.current?.stopRecording();
        setRecording(false);
        if (!result) {
          flashToast('录屏未保存：未捕获到数据');
          return;
        }
        flashToast('正在保存到 PC…');
        const { meta } = await filesApi.upload({
          serial,
          kind: 'recordings',
          ext: result.ext,
          prefix: 'record',
          blob: result.blob,
        });
        const mb = (meta.bytes ?? result.blob.size) / 1024 / 1024;
        flashToast(`录屏已保存（${mb.toFixed(1)} MB）`);
      }
    } catch (e) {
      // If the recorder failed mid-session, force the toggle back to off
      // so the user can retry without an inconsistent UI.
      if (recording) setRecording(false);
      flashToast(`录屏失败：${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const sideToolsWidth = useMirror((s) => s.sideToolsWidth);
  const setSideToolsWidth = useMirror((s) => s.setSideToolsWidth);
  /**
   * When the rail is at least `SIDE_TOOLS_LABEL_THRESHOLD` wide, every
   * `ToolbarBtn` switches from icon-only (centered, square) to a full-
   * width row with `[icon][label]` so the user can read what each
   * control does without hovering. The threshold matches the dragger's
   * "first label-pixel appears" moment, giving the user clear feedback
   * that they crossed the boundary.
   */
  const expanded = sideToolsWidth >= SIDE_TOOLS_LABEL_THRESHOLD;
  const groupGap = expanded ? 'gap-0.5' : 'gap-1';

  /**
   * Vertical resize handle. Lives on the LEFT edge of the rail (so the
   * user drags it leftwards to grow the rail / rightwards to shrink).
   * Captures the pointer so a fast drag past the edge doesn't lose the
   * gesture, and runs entirely off `clientX` (no relative deltas) so a
   * jitter in pointermove ordering can't drift the width over time.
   */
  const dragStateRef = useRef<{ startX: number; startW: number } | null>(null);
  const onDragHandle = (e: React.PointerEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    dragStateRef.current = { startX: e.clientX, startW: sideToolsWidth };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onDragHandleMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const st = dragStateRef.current;
    if (!st) return;
    e.preventDefault();
    // Rail is on the right of the window → dragging LEFT (clientX
    // decreasing) increases width. The store clamps to MIN/MAX so we
    // don't bother bounding here.
    const next = st.startW + (st.startX - e.clientX);
    setSideToolsWidth(next);
  };
  const onDragHandleEnd = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!dragStateRef.current) return;
    dragStateRef.current = null;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* pointer already released by a follow-on event — fine */
    }
  };

  return (
    <div
      className="relative flex shrink-0 select-none flex-col items-stretch overflow-y-auto border-l border-line bg-bg-card/95 pl-2.5 pr-1 py-2"
      style={{ width: sideToolsWidth, minWidth: SIDE_TOOLS_MIN_W, maxWidth: SIDE_TOOLS_MAX_W }}
    >
      {/*
        Resize gutter on the LEFT edge. A 4-px wide invisible strip with
        a faint hover state — enough surface for an easy grab without
        eating canvas pixels. Hover band is layered above the strip so
        the cursor change is felt instantly.
      */}
      <div
        className="absolute left-0 top-0 z-30 h-full w-1.5 cursor-col-resize"
        onPointerDown={onDragHandle}
        onPointerMove={onDragHandleMove}
        onPointerUp={onDragHandleEnd}
        onPointerCancel={onDragHandleEnd}
        title="拖动调整工具栏宽度"
      >
        <div className="h-full w-px translate-x-[3px] bg-line/40 hover:bg-accent/70" />
      </div>

      {/*
        Group 1 — 方向: device rotate + screen orientation lock. Sits at
        the very top because rotation is the most disruptive change a
        user can make from the rail (it triggers a server reconnect in
        the lock-orientation case and a full re-aspect for a free
        rotate), so we keep it visually separate above the hardware
        keys.
      */}
      <SideGroup
        id="orientation"
        label="方向"
        gap={groupGap}
        expanded={expanded}
        collapsed={!!sideToolsCollapsed['orientation']}
        onToggle={() => toggleSideToolsGroup('orientation')}
      >
        <ToolbarBtn
          icon={RotateCw}
          label="旋转设备"
          tooltip="即时旋转设备画面（不重连）"
          onClick={() => sendControl(packRotateDevice())}
          disabled={!live}
          expanded={expanded}
        />
        {/*
          Orientation lock select. Vertical rail width is unpredictable so
          the dropdown is full-width — the displayed text truncates at
          narrow rails, but expanding the rail or hovering reveals the
          full label, and the dropdown always shows every option in full
          when opened.
        */}
        <select
          className="h-7 w-full rounded border border-line bg-bg px-1.5 text-[11px] text-ink"
          value={String(lockOrientation)}
          onChange={(e) => setLockOrientation(Number(e.target.value) as LockOrientation)}
          onPointerDown={(e) => e.stopPropagation()}
          title="锁定画面方向（重连）"
        >
          <option value="-1">方向自由</option>
          <option value="0">锁 0°</option>
          <option value="1">锁 90°</option>
          <option value="2">锁 180°</option>
          <option value="3">锁 270°</option>
        </select>
      </SideGroup>

      <Divider />

      {/*
        Group 2 — 硬件键: vol+ / vol- / power. Mirrors the location of
        the real phone's side buttons so muscle memory transfers.
      */}
      <SideGroup
        id="hardware"
        label="硬件键"
        gap={groupGap}
        expanded={expanded}
        collapsed={!!sideToolsCollapsed['hardware']}
        onToggle={() => toggleSideToolsGroup('hardware')}
      >
        <ToolbarBtn
          icon={Volume2}
          label="音量+"
          onClick={() => tapKey(KEYCODE.VOLUME_UP)}
          disabled={!live}
          expanded={expanded}
        />
        <ToolbarBtn
          icon={VolumeX}
          label="音量-"
          onClick={() => tapKey(KEYCODE.VOLUME_DOWN)}
          disabled={!live}
          expanded={expanded}
        />
        <ToolbarBtn
          icon={Power}
          label="电源"
          onClick={() => tapKey(KEYCODE.POWER)}
          disabled={!live}
          expanded={expanded}
        />
      </SideGroup>

      <Divider />

      {/*
        Group 3 — 系统: notification / quick-settings / display power /
        stay-awake. The latter moved here from the deleted bottom config
        bar — it's a startup-time scrcpy flag (changing it triggers a
        reconnect) but conceptually it's a "device-side system tweak"
        like the panels and display-off toggles.
      */}
      <SideGroup
        id="system"
        label="系统"
        gap={groupGap}
        expanded={expanded}
        collapsed={!!sideToolsCollapsed['system']}
        onToggle={() => toggleSideToolsGroup('system')}
      >
        <ToolbarBtn
          icon={Bell}
          label={expandedPanel === 'notif' ? '收起通知' : '通知面板'}
          tooltip={expandedPanel === 'notif' ? '收起通知面板' : '展开通知面板'}
          onClick={onToggleNotifPanel}
          disabled={!live}
          highlighted={expandedPanel === 'notif'}
          expanded={expanded}
        />
        <ToolbarBtn
          icon={Settings}
          label={expandedPanel === 'settings' ? '收起设置' : '设置面板'}
          tooltip={expandedPanel === 'settings' ? '收起设置面板' : '展开设置面板'}
          onClick={onToggleSettingsPanel}
          disabled={!live}
          highlighted={expandedPanel === 'settings'}
          expanded={expanded}
        />
        <ToolbarBtn
          icon={displayOff ? ScreenShareOff : ScreenShare}
          label={displayOff ? '点亮屏幕' : '关闭屏幕'}
          tooltip={displayOff ? '点亮设备屏幕' : '关闭设备屏幕'}
          onClick={onToggleDisplayPower}
          disabled={!live}
          highlighted={displayOff}
          expanded={expanded}
        />
        <ToolbarBtn
          icon={Coffee}
          label={stayAwake ? '常亮：开' : '常亮：关'}
          tooltip={stayAwake ? '关闭常亮（重连生效）' : '开启常亮（重连生效）'}
          onClick={() => setStayAwake(!stayAwake)}
          highlighted={stayAwake}
          expanded={expanded}
        />
      </SideGroup>

      <Divider />

      {/*
        Group 4 — 剪贴板: 单一入口。原本三个按钮（拉取 / 粘贴 / 历史）
        被压成一个「剪贴板」按钮，点击展开右上角的双栏抽屉，里面同时
        承载历史列表 + 拉取 + 粘贴 + 编辑 + 推送 + 复制。
        Ctrl+C / Ctrl+V 快捷键仍然由 FloatingMirror 注册（不依赖
        toolbar 上的按钮存在）。
      */}
      <SideGroup
        id="clipboard"
        label="剪贴板"
        gap={groupGap}
        expanded={expanded}
        collapsed={!!sideToolsCollapsed['clipboard']}
        onToggle={() => toggleSideToolsGroup('clipboard')}
      >
        <ToolbarBtn
          icon={Clipboard}
          label="剪贴板"
          tooltip="打开剪贴板抽屉（历史 / 拉取 / 粘贴 / 推送）"
          onClick={onOpenClipHistory}
          highlighted={clipHistoryOpen}
          expanded={expanded}
        />
      </SideGroup>

      <Divider />

      {/* Group 5 — 捕获: screenshot + record. Record toggles state. */}
      <SideGroup
        id="capture"
        label="捕获"
        gap={groupGap}
        expanded={expanded}
        collapsed={!!sideToolsCollapsed['capture']}
        onToggle={() => toggleSideToolsGroup('capture')}
      >
        <ToolbarBtn
          icon={Camera}
          label="截屏"
          onClick={onScreenshot}
          disabled={busy}
          expanded={expanded}
        />
        <ToolbarBtn
          icon={recording ? Square : Video}
          label={recording ? `停止 ${formatElapsed(recElapsed)}` : '录屏'}
          onClick={onToggleRecord}
          disabled={busy && !recording /* allow stop even while uploading */}
          highlighted={recording}
          expanded={expanded}
        />
      </SideGroup>

      <Divider />

      {/*
        Group 6 — App: act on the foreground package. Mildly destructive
        (clear data) is guarded by a modal; launcher / systemui is
        filtered out with a friendly toast inside `withForegroundPkg`.
      */}
      <SideGroup
        id="app"
        label="App"
        gap={groupGap}
        expanded={expanded}
        collapsed={!!sideToolsCollapsed['app']}
        onToggle={() => toggleSideToolsGroup('app')}
      >
        <ToolbarBtn
          icon={RefreshCw}
          label="重启 App"
          tooltip="重启前台 App"
          onClick={onRestartApp}
          disabled={!live || appBusy}
          expanded={expanded}
        />
        <ToolbarBtn
          icon={Trash2}
          label="清除数据"
          tooltip="清除前台 App 数据"
          onClick={onRequestClearData}
          disabled={!live || appBusy}
          expanded={expanded}
        />
        <ToolbarBtn
          icon={AppWindow}
          label="App 设置"
          tooltip="打开前台 App 系统设置"
          onClick={onOpenAppSettings}
          disabled={!live || appBusy}
          expanded={expanded}
        />
      </SideGroup>

      <Divider />

      {/*
        Group 7 — 其他: HUD + logcat overlay. Reconnect moved up to the
        DragHeader (alongside the quality select) since both imply
        "rebuild the WebSocket session", so it didn't fit "其他" anymore.
      */}
      <SideGroup
        id="other"
        label="其他"
        gap={groupGap}
        expanded={expanded}
        collapsed={!!sideToolsCollapsed['other']}
        onToggle={() => toggleSideToolsGroup('other')}
      >
        <ToolbarBtn
          icon={Activity}
          label={hudVisible ? '隐藏性能' : '显示性能'}
          tooltip={hudVisible ? '隐藏性能指标 (FPS/码率/延迟)' : '显示性能指标 (FPS/码率/延迟)'}
          onClick={toggleHud}
          highlighted={hudVisible}
          expanded={expanded}
        />
        <ToolbarBtn
          icon={ScrollText}
          label="日志"
          onClick={toggleLogcat}
          highlighted={showLogcat}
          expanded={expanded}
        />
      </SideGroup>

      {toast && (
        <div className="pointer-events-none absolute left-1/2 top-2 z-10 -translate-x-1/2 whitespace-nowrap rounded bg-black/85 px-3 py-1 text-[11px] text-white shadow">
          {toast}
        </div>
      )}

      {clearConfirm && (
        <ClearDataModal
          pkg={clearConfirm.pkg}
          onCancel={() => setClearConfirm(null)}
          onConfirm={() => void onConfirmClearData()}
        />
      )}
    </div>
  );
}

/* ===================================================================== */
/* Helpers                                                                */
/* ===================================================================== */

/**
 * Collapsible side-rail group. Renders a clickable header with a
 * chevron + label, and only emits the children when the group is
 * expanded. Header is always shown so the user has a click target to
 * re-expand a collapsed group.
 *
 *   ▾ 方向          ← expanded: chevron-down + label, children below
 *   ▸ 方向          ← collapsed: chevron-right + label, no children
 *
 * In icon-only rail width (`expanded === false` here), the header
 * shrinks to a centered chevron with the label kept in `title` so
 * hovering still surfaces the group name.
 */
function SideGroup({
  id,
  label,
  expanded,
  collapsed,
  onToggle,
  children,
  gap = 'gap-1',
}: {
  id: string;
  label: string;
  expanded: boolean;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  gap?: string;
}): JSX.Element {
  const Chevron = collapsed ? ChevronRight : ChevronDown;
  return (
    <div className="flex flex-col" data-side-group={id}>
      <button
        type="button"
        className={clsx(
          'mb-0.5 flex h-5 shrink-0 items-center rounded text-[10px] uppercase tracking-wide text-ink-muted',
          'hover:bg-bg hover:text-ink',
          expanded ? 'gap-1 px-1' : 'justify-center px-0',
        )}
        onClick={onToggle}
        onPointerDown={(e) => e.stopPropagation()}
        title={collapsed ? `展开 ${label}` : `折叠 ${label}`}
        aria-expanded={!collapsed}
      >
        <Chevron className="h-3 w-3 shrink-0" />
        {expanded && <span className="truncate normal-case tracking-normal">{label}</span>}
      </button>
      {!collapsed && (
        <div className={clsx('flex flex-col items-stretch', gap)}>{children}</div>
      )}
    </div>
  );
}

function Divider(): JSX.Element {
  return <div className="my-1 h-px shrink-0 bg-line/70" />;
}

/**
 * Centered overlay modal used by the Phase 7 "clear app data" flow.
 * Inline to avoid pulling in a modal library and to stay inside the
 * portal layer the floating mirror already owns (works inside the PiP
 * window for free).
 */
function ClearDataModal({
  pkg,
  onCancel,
  onConfirm,
}: {
  pkg: string;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60"
      onPointerDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-80 max-w-[90vw] rounded-md border border-line bg-bg-card p-4 shadow-2xl">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-400" />
          <div className="flex-1">
            <div className="text-sm font-medium text-ink">清除 App 数据</div>
            <div className="mt-1 text-xs leading-relaxed text-ink-muted">
              将清除 <span className="font-mono text-ink">{pkg}</span>{' '}
              的全部数据（登录态 / 缓存 / 配置都会丢失），是否继续？
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            className="rounded border border-line px-3 py-1 text-xs hover:bg-bg"
            onClick={onCancel}
          >
            取消
          </button>
          <button
            className="rounded border border-rose-500 bg-rose-500/15 px-3 py-1 text-xs text-rose-200 hover:bg-rose-500/30"
            onClick={onConfirm}
            autoFocus
          >
            继续清除
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Side-rail / config-bar button. Has two layouts:
 *   - `expanded === false` (default) → square 28×28 icon-only button.
 *     Used when the rail is narrower than `SIDE_TOOLS_LABEL_THRESHOLD`
 *     and on the bottom config bar (where buttons sit alongside selects).
 *   - `expanded === true` → full-width row with `[icon][label]`, label
 *     truncates so really long Chinese strings still fit at modest
 *     rail widths. The `title` (and aria-label) always carries the
 *     long-form description for hover-tooltip + screen readers.
 */
function ToolbarBtn({
  icon: Icon,
  label,
  tooltip,
  onClick,
  disabled,
  highlighted,
  expanded = false,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  /** Long-form description for tooltip / aria-label. Defaults to `label`. */
  tooltip?: string;
  onClick: () => void;
  disabled?: boolean;
  highlighted?: boolean;
  expanded?: boolean;
}): JSX.Element {
  const title = tooltip ?? label;
  const sharedColor = highlighted
    ? 'border-rose-500/70 bg-rose-500/15 text-rose-300'
    : 'border-line text-ink';
  if (expanded) {
    return (
      <button
        type="button"
        className={clsx(
          'flex h-7 w-full items-center gap-1.5 rounded border px-1.5',
          'hover:bg-bg disabled:opacity-40 disabled:cursor-not-allowed',
          sharedColor,
        )}
        onClick={onClick}
        onPointerDown={(e) => e.stopPropagation()}
        disabled={disabled}
        title={title}
        aria-label={title}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate text-[11px]">{label}</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      className={clsx(
        'inline-flex h-7 w-7 shrink-0 items-center justify-center self-center rounded border',
        'hover:bg-bg disabled:opacity-40 disabled:cursor-not-allowed',
        sharedColor,
      )}
      onClick={onClick}
      onPointerDown={(e) => e.stopPropagation()}
      disabled={disabled}
      title={title}
      aria-label={title}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

/** Compact 24×24 button used only in the drag header. */
function WindowBtn({
  icon: Icon,
  label,
  onClick,
  active,
  disabled,
  variant = 'default',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  variant?: 'default' | 'danger';
}): JSX.Element {
  return (
    <button
      className={clsx(
        'inline-flex h-6 w-6 items-center justify-center rounded text-[10px]',
        active && 'bg-accent/30 text-accent',
        !active && !disabled && variant === 'default' && 'hover:bg-bg',
        !disabled && variant === 'danger' && 'hover:bg-rose-500/20 hover:text-rose-300',
        disabled && 'cursor-not-allowed opacity-40',
      )}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={onClick}
      disabled={disabled}
      title={label}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

function formatElapsed(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
